<template>
  <WdsStack direction-row align-center gap-sm>
    <div
      :class="{
        'tw-text-gray-700': managedStatus === TrustedContactManagedStatus.Unmanaged,
      }"
    >
      <slot name="name">{{ name }}</slot>
    </div>

    <WdsTooltip
      v-if="managedStatus === TrustedContactManagedStatus.Unmanaged"
      top
      data-test="worker-is-unclaimed-status"
      global
    >
      <WdsIcon color="#0E0D12" name="warning" weight="thick" :size="12" />

      <template #tooltip-content>
        <WdsStack direction-column gap-sm>
          <WdsStack direction-row align-center justify-center gap-xs>
            <WdsIcon name="warning" weight="thick" :size="14" class="text-gray-700" />
            <div>{{ $t('Unclaimed account') }}</div>
          </WdsStack>

          <div>
            {{
              $t(
                'This account was previously managed by a staffing agency, but the ownership has expired. To re-engage, please onboard the new owner to claim this account.',
              )
            }}
          </div>
        </WdsStack>
      </template>
    </WdsTooltip>

    <WdsTooltip
      v-if="recruiterOwnershipDays && recruiterOwnershipDays > 0"
      right
      :data-test="
        managedStatus === TrustedContactManagedStatus.StaffingAgency
          ? 'worker-managed-status'
          : 'worker-self-managed-status'
      "
    >
      <WdsIcon color="#0E0D12" name="group" weight="thick" :size="12" />

      <template #tooltip-content>
        <WdsStack direction-column gap-sm>
          <WdsStack direction-column>
            <div v-if="managesWorkers">{{ $t('Worker managed by') }}</div>
            <div v-else>{{ $t('Ongoing ownership period for') }}</div>
            <div>{{ recruiterName }}</div>
          </WdsStack>

          <WdsStack direction-column>
            <div>{{ $t('Ownership ends:') }}</div>
            <div>
              {{ ownershipEndsAt }}
              <span v-if="recruiterOwnershipStartDate && days">({{ days }})</span>
            </div>
          </WdsStack>

          <WdsStack v-if="!managesWorkers" direction-row>
            <div>{{ $t('Ownership fee:') }}</div>
            <div>{{ recruiterFeePercentage ?? '--' }} %</div>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsTooltip>

    <slot name="label">
      <div v-if="showLabel && recruiterName">
        <WdsLabel sm subtle>{{ recruiterName }}</WdsLabel>
      </div>
    </slot>
  </WdsStack>
</template>

<script setup lang="ts">
import moment from 'moment'
import { computed } from 'vue'
import { WdsIcon, WdsLabel, WdsStack, WdsTooltip } from '@worksome/wds-components'

import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import { TrustedContactManagedStatus } from '@/gql/graphql'

const props = withDefaults(
  defineProps<{
    name?: string | null
    managesWorkers?: boolean
    managedStatus?: TrustedContactManagedStatus | null
    recruiterName?: string | null
    recruiterFeePercentage?: number | null
    recruiterOwnershipStartDate?: string | null
    recruiterOwnershipDays?: number | null
    recruiterOwnershipDaysLeft?: number | null
    showLabel?: boolean
  }>(),
  {
    name: undefined,
    managesWorkers: false,
    managedStatus: undefined,
    recruiterName: undefined,
    recruiterFeePercentage: undefined,
    recruiterOwnershipStartDate: undefined,
    recruiterOwnershipDays: undefined,
    recruiterOwnershipDaysLeft: undefined,
    showLabel: false,
  },
)

const days = computed<string>(() => {
  if (!props.recruiterOwnershipDaysLeft) {
    return ''
  }

  return $tc(':count day | :count days', props.recruiterOwnershipDaysLeft)
})

const ownershipEndsAt = computed<string>(() => {
  if (!props.recruiterOwnershipStartDate) {
    return '--'
  }

  return moment(props.recruiterOwnershipStartDate)
    .add(props.recruiterOwnershipDays, 'days')
    .format('MMM DD, Y')
})
</script>
